document.documentElement.classList.add('js');

const menuToggle = document.querySelector('[data-menu-toggle]');
const mobileMenu = document.querySelector('[data-mobile-menu]');

if (menuToggle && mobileMenu) {
  menuToggle.addEventListener('click', () => {
    const open = menuToggle.getAttribute('aria-expanded') === 'true';
    menuToggle.setAttribute('aria-expanded', String(!open));
    mobileMenu.hidden = open;
    document.body.classList.toggle('menu-open', !open);
  });

  mobileMenu.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => {
      menuToggle.setAttribute('aria-expanded', 'false');
      mobileMenu.hidden = true;
      document.body.classList.remove('menu-open');
    });
  });
}

const revealItems = document.querySelectorAll('.reveal');
if ('IntersectionObserver' in window) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px' });
  revealItems.forEach((item) => observer.observe(item));
} else {
  revealItems.forEach((item) => item.classList.add('is-visible'));
}

document.querySelectorAll('[data-product-form]').forEach((form) => {
  const select = form.querySelector('[data-variant-select]');
  const price = form.closest('[data-product-root]')?.querySelector('[data-product-price]');
  const button = form.querySelector('[data-add-button]');

  if (!select) return;
  select.addEventListener('change', () => {
    const option = select.options[select.selectedIndex];
    if (price && option.dataset.price) price.textContent = option.dataset.price;
    if (button) {
      const available = option.dataset.available === 'true';
      button.disabled = !available;
      button.textContent = available ? 'Add to bag' : 'Sold out';
    }
  });
});

class LymdaeComparison extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('input[type="range"]');
    if (!input || this.dataset.ready === 'true') return;

    const update = () => {
      const position = Number(input.value);
      this.style.setProperty('--compare-position', `${position}%`);
      input.setAttribute('aria-valuetext', `${position}% LYMDAÊ matcha shown`);
    };

    input.addEventListener('input', update);
    update();
    this.dataset.ready = 'true';
  }
}

if (!customElements.get('lymdae-comparison')) {
  customElements.define('lymdae-comparison', LymdaeComparison);
}

class LymdaeCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;

    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.currentLabel = this.querySelector('[data-carousel-current]');
    this.previousButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.duration = Number(this.dataset.duration) || 5200;
    this.index = 0;
    this.inView = true;
    this.isHovered = false;
    this.hasFocus = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.slides.length < 2) return;

    this.show = (nextIndex, userInitiated = false) => {
      this.index = (nextIndex + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });

      if (this.currentLabel) {
        this.currentLabel.textContent = String(this.index + 1).padStart(2, '0');
      }

      if (userInitiated) this.restart();
    };

    this.stop = () => {
      window.clearInterval(this.timer);
      this.timer = undefined;
    };

    this.start = () => {
      this.stop();
      if (this.reducedMotion || !this.inView || this.isHovered || this.hasFocus || document.hidden) return;
      this.timer = window.setInterval(() => this.show(this.index + 1), this.duration);
    };

    this.restart = () => {
      this.stop();
      this.start();
    };

    this.previousButton?.addEventListener('click', () => this.show(this.index - 1, true));
    this.nextButton?.addEventListener('click', () => this.show(this.index + 1, true));

    this.addEventListener('mouseenter', () => {
      this.isHovered = true;
      this.stop();
    });
    this.addEventListener('mouseleave', () => {
      this.isHovered = false;
      this.start();
    });
    this.addEventListener('focusin', () => {
      this.hasFocus = true;
      this.stop();
    });
    this.addEventListener('focusout', () => {
      window.requestAnimationFrame(() => {
        this.hasFocus = this.contains(document.activeElement);
        this.start();
      });
    });

    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') this.pointerStart = event.clientX;
    });
    this.addEventListener('pointerup', (event) => {
      if (event.pointerType !== 'touch' || this.pointerStart === undefined) return;
      const distance = event.clientX - this.pointerStart;
      this.pointerStart = undefined;
      if (Math.abs(distance) < 45) return;
      this.show(this.index + (distance < 0 ? 1 : -1), true);
    });

    this.handleVisibility = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this.handleVisibility);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.inView ? this.start() : this.stop();
      }, { threshold: 0.2 });
      this.observer.observe(this);
    }

    this.show(0);
    this.start();
    this.dataset.ready = 'true';
  }

  disconnectedCallback() {
    this.stop?.();
    this.observer?.disconnect();
    if (this.handleVisibility) document.removeEventListener('visibilitychange', this.handleVisibility);
  }
}

if (!customElements.get('lymdae-carousel')) {
  customElements.define('lymdae-carousel', LymdaeCarousel);
}

document.querySelectorAll('[data-method-tabs]').forEach((root) => {
  const tabs = Array.from(root.querySelectorAll('[data-method-tab]'));
  const panels = Array.from(root.querySelectorAll('[data-method-panel]'));
  if (!tabs.length || !panels.length) return;

  const activate = (name, moveFocus = false) => {
    tabs.forEach((tab) => {
      const active = tab.dataset.methodTab === name;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active && moveFocus) tab.focus();
    });

    panels.forEach((panel) => {
      panel.hidden = panel.dataset.methodPanel !== name;
    });
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => activate(tab.dataset.methodTab));
    tab.addEventListener('keydown', (event) => {
      let nextIndex;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      if (nextIndex === undefined) return;
      event.preventDefault();
      activate(tabs[nextIndex].dataset.methodTab, true);
    });
  });

  const selected = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
  activate(selected.dataset.methodTab);
});

const supportRoot = document.querySelector('[data-support-root]');

if (supportRoot) {
  const panel = supportRoot.querySelector('[data-support-panel]');
  const overlay = supportRoot.querySelector('[data-support-overlay]');
  const closeButton = supportRoot.querySelector('[data-support-close]');
  const openButtons = Array.from(document.querySelectorAll('[data-support-open]'));
  let lastTrigger;

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const setExpanded = (expanded) => {
    openButtons.forEach((button) => button.setAttribute('aria-expanded', String(expanded)));
  };

  const openSupport = (trigger) => {
    if (!panel || !overlay) return;
    lastTrigger = trigger || document.activeElement;
    panel.hidden = false;
    overlay.hidden = false;
    document.body.classList.add('support-open');
    setExpanded(true);
    window.requestAnimationFrame(() => closeButton?.focus());
  };

  const closeSupport = () => {
    if (!panel || !overlay) return;
    panel.hidden = true;
    overlay.hidden = true;
    document.body.classList.remove('support-open');
    setExpanded(false);
    if (lastTrigger instanceof HTMLElement) lastTrigger.focus();
  };

  openButtons.forEach((button) => {
    button.setAttribute('aria-controls', 'SupportConcierge');
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-expanded', 'false');
    button.addEventListener('click', () => openSupport(button));
  });

  closeButton?.addEventListener('click', closeSupport);
  overlay?.addEventListener('click', closeSupport);

  panel?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSupport();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = Array.from(panel.querySelectorAll(focusableSelector));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  if (window.location.hash === '#SupportConcierge' && supportRoot.querySelector('[data-support-state]')) {
    openSupport();
  }
}

if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  document.querySelectorAll('video[autoplay]').forEach((video) => video.pause());
}
